'use client';

import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Link } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import type { BlogPost } from '@/types/content';

interface BlogCardProps {
  post: BlogPost;
  compact?: boolean;
  variant?: 'default' | 'horizontal';
  className?: string;
}

export function BlogCard({ post, compact = false, variant = 'default', className }: BlogCardProps) {
  const t = useTranslations('Blog');

  // Extract minutes from readingTime string (e.g., "5 min read" -> 5)
  const minutes = parseInt(post.readingTime.match(/\d+/)?.[0] || '0', 10);
  const readingTimeText = t('readingTime', { minutes });

  // Horizontal variant for desktop, vertical on mobile
  if (variant === 'horizontal') {
    return (
      <Link href={`/blog/${post.slug}`} className="block">
        <motion.div whileHover={{ y: -3 }} transition={{ duration: 0.2 }} className={className}>
          <Card className="overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:bg-card/80 hover:border-primary/50 transition-all duration-300 shadow-sm hover:shadow-md group flex flex-col md:flex-row">
            {/* Image - left side on desktop, top on mobile */}
            {post.image && (
              <div className="relative w-full md:w-[35%] aspect-video md:aspect-[4/3] overflow-hidden flex-shrink-0">
                <Image
                  src={post.image}
                  alt={post.title}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-background/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            )}

            {/* Content - right side on desktop */}
            <div className="flex flex-col flex-1 p-5 md:p-6">
              {/* Top: Tags + Reading Time */}
              <div className="flex items-center gap-2 mb-3">
                {post.tags.slice(0, 1).map((tag) => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className="text-xs font-normal border-primary/20 bg-primary/5 text-primary capitalize"
                  >
                    {tag}
                  </Badge>
                ))}
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {readingTimeText}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-lg md:text-xl font-bold leading-tight tracking-tight group-hover:text-primary transition-colors mb-2">
                {post.title}
              </h3>

              {/* Description */}
              <p className="text-muted-foreground text-sm line-clamp-2 leading-relaxed mb-auto">
                {post.description}
              </p>

              {/* Bottom: Date + Arrow */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/50">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>
                    {new Date(post.date).toLocaleDateString(post.locale, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>

                <div className="flex items-center text-primary text-sm font-medium opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                  {t('readMore')}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </Link>
    );
  }

  // Default vertical variant
  return (
    <Link href={`/blog/${post.slug}`} className="block h-full">
      <motion.div
        whileHover={{ y: -5 }}
        transition={{ duration: 0.2 }}
        className={cn('h-full', className)}
      >
        <Card className="h-full overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:bg-card/80 hover:border-primary/50 transition-all duration-300 shadow-sm hover:shadow-md group flex flex-col">
          {post.image && (
            <div className="relative w-full aspect-video overflow-hidden">
              <Image
                src={post.image}
                alt={post.title}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          )}

          <CardHeader className={cn('space-y-2', compact ? 'p-4' : 'p-6')}>
            <div className="flex flex-wrap gap-2 mb-1">
              {post.tags.slice(0, 2).map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="text-xs font-normal border-primary/20 bg-primary/5 text-primary capitalize"
                >
                  {tag}
                </Badge>
              ))}
            </div>
            <h3
              className={cn(
                'font-bold leading-tight tracking-tight group-hover:text-primary transition-colors',
                compact ? 'text-lg' : 'text-xl',
              )}
            >
              {post.title}
            </h3>
          </CardHeader>

          <CardContent className={cn('flex-grow', compact ? 'px-4 pb-2' : 'px-6 pb-4')}>
            <p className="text-muted-foreground text-sm line-clamp-2 leading-relaxed">
              {post.description}
            </p>
          </CardContent>

          <CardFooter
            className={cn(
              'flex items-center justify-between text-xs text-muted-foreground border-t border-border/50',
              compact ? 'p-4' : 'p-6 py-4',
            )}
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                <span>
                  {new Date(post.date).toLocaleDateString(post.locale, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>{readingTimeText}</span>
              </div>
            </div>

            <div className="flex items-center text-primary font-medium opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
              {t('readMore')}
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </div>
          </CardFooter>
        </Card>
      </motion.div>
    </Link>
  );
}
